import { useLazyLoadQuery } from 'react-relay';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import RelatedContentSampleQuery, {
	type useRelatedContentQuery,
	type useRelatedContentQuery$variables,
} from '../hooks/__generated__/useRelatedContentQuery.graphql.ts';
import {
	buildRelatedContentSampleVariables,
	normalizeRelatedContentSsrState,
	type RelatedContentSsrState,
} from './relatedContentSsrState.ts';

// Read with these only when there is nothing to look up, so the exact value never keys a real store
// read. A store-only read never touches the network, so a dummy lookup is free.
const SKIPPED_VARIABLES: useRelatedContentQuery$variables = {
	cypherQuery: '',
	queryContext: '',
	version: 'V3',
	first: 0,
};

// Read the preload without starting another request.
export const useRelatedContentSsrState = (
	contentId: string | undefined,
	contentType?: BylineContentType,
): RelatedContentSsrState | undefined => {
	const { cloudId, activationId } = useSessionData();
	const variables =
		!contentId || !cloudId || !activationId
			? undefined
			: buildRelatedContentSampleVariables(cloudId, activationId, contentId, contentType);

	const data = useLazyLoadQuery<useRelatedContentQuery>(
		RelatedContentSampleQuery,
		variables ?? SKIPPED_VARIABLES,
		{ fetchPolicy: 'store-only' },
	);

	if (!contentId || variables === undefined || data?.graphStore_cypherQueryV2 == null) {
		return undefined;
	}

	return normalizeRelatedContentSsrState(contentId, data);
};
